import { BRAND_BASE_DOMAIN, workflowCampaignStateSchema, workflowStopResultSchema, workflowRuleRestoreResultSchema, workflowsListResponseSchema, crmAllWorkflowRunsResponseSchema, workflowEditorResponseSchema, workflowSavedResponseSchema, workflowRunStartSchema, workflowRunDetailSchema, workflowEditorDefinitionSchema, workflowSavedRunsResponseSchema } from '@easyflow/shared';
import { shouldPrefixTenant } from '@easyflow/ui-frontend';
import { getLoginPath } from '@/lib/paths';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { resolveApiErrorMessage } from '@/lib/api-error-message';
import type {
  BoardWorkflowRunsResponse,
  BoardWorkflowStatsResponse,
  BoardWorkflowTriggersResponse,
  CdrRecord,
  ConferenceRoom,
  CrmAllWorkflowRunsResponse,
  CrmCampaignWorkflowLinksResponse,
  CrmCampaignWorkflowRunsResponse,
  CrmCampaignWorkflowStatsResponse,
  CrmCampaignWorkflowTriggersResponse,
  IvrMenu,
  Queue,
  RingGroup,
  SipExtensionListQueryInput,
  SipExtensionReadDto,
  SipExtensionsListData,
  SipTrunkWire,
  TaskSettingsAutomationLinksResponse,
  TaskSettingsFormLinksResponse,
  TaskSettingsWorkflowLinksResponse,
  TaskSettingsWorkflowRunsResponse,
  TenantsListData,
  VoicemailBox,
  WorkflowsListResponse,
  LifecycleEvent as LifecycleEventDto,
  LifecyclePage as LifecyclePageDto,
  LifecycleQueryParams as LifecycleQueryParamsDto,
  ProductSnapshot as ProductSnapshotDto,
  StockReconciliationReport as StockReconciliationReportDto,
  StoreSipTrunkSettings,
} from '@easyflow/shared';
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

/**
 * Single source of truth per l'host pubblico mostrato all'utente (snippet
 * cURL, URL webhook, embed widget). Deriva sempre dall'origin live; in dev
 * (localhost) ricade sull'apex pubblico cosi' gli esempi restano copia-incolla
 * corretti. Sostituisce un host phantom (sottodominio `api`) hardcoded.
 */
export function getPublicBaseUrl(): string {
  if (typeof window === 'undefined') return `https://${BRAND_BASE_DOMAIN}`;
  return window.location.hostname === 'localhost'
    ? `https://${BRAND_BASE_DOMAIN}`
    : window.location.origin.replace(/:\d+$/, '');
}

/**
 * Origin canonici della PIATTAFORMA (dominio brand base, apex + `app.`),
 * indipendenti dall'origin corrente del browser. Servono ai listener
 * `postMessage` dei flussi OAuth: i redirect/callback restano sul dominio
 * canonico (decisione A3, Task #3257), quindi con la SPA servita su un
 * dominio prodotto il messaggio arriva cross-origin da uno di questi.
 * NON usare `getPublicBaseUrl()` per questo scopo: in produzione risolve
 * all'origin live del browser, non al canonico.
 */
export function getCanonicalPlatformOrigins(): ReadonlyArray<string> {
  const base: string = BRAND_BASE_DOMAIN;
  return [`https://${base}`, `https://app.${base}`];
}

/**
 * True se `origin` è fidato per i postMessage OAuth: same-origin della SPA
 * oppure uno degli origin canonici della piattaforma (match esatto).
 */
export function isTrustedOAuthMessageOrigin(origin: string): boolean {
  if (typeof window !== 'undefined' && origin === window.location.origin) return true;
  return getCanonicalPlatformOrigins().includes(origin);
}

/**
 * Display host del registrar/proxy SIP pubblico verso il SBC. Coerente col SoT
 * backend `getSipPublicConfig().sipServer` (env `SIP_PUBLIC_SERVER`). NON è
 * l'apex `easyflowai.it` (Cloudflare-proxied, solo HTTP, non raggiunge il SBC).
 * Usato dove non c'è una fetch runtime della config SIP (es. wizard setup
 * tenant); le superfici con contesto tenant preferiscono `/sip-public-config`.
 */
export const SIP_PUBLIC_SERVER_HOST = `sip.${BRAND_BASE_DOMAIN}`;

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  /** Structured business error code retained when an HTTP-200 envelope is used. */
  error_code?: string;
  message?: string;
  timestamp: string;
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  request_id?: string;
}


interface ModuleDisabledError extends Error {
  status: number;
}

/**
 * Task #1245 — typed shape for any error thrown by `apiClient`. Consumers
 * can narrow with `isApiError(err)` and read `err.requestId` / `err.status`
 * without `as any` escapes.
 */
export interface ApiError extends Error {
  status?: number;
  requestId?: string;
  data?: unknown;
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof Error;
}

function trustedGatewayApplicationError(text: string, contentType: string): {
  payload: Record<string, unknown>;
  message: string;
} | null {
  if (!text || !contentType.includes('application/json')) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object') return null;
    const payload = parsed as Record<string, unknown>;
    const nestedError = payload.error;
    if (!nestedError || typeof nestedError !== 'object') return null;
    const error = nestedError as Record<string, unknown>;
    if (error.code !== 'INVENTORY_SESSION_SCHEMA_INCOMPATIBLE') return null;
    const message = typeof error.message === 'string'
      ? error.message
      : typeof payload.message === 'string'
        ? payload.message
        : '';
    return message ? { payload, message } : null;
  } catch {
    return null;
  }
}

// Last seen X-Request-ID — stored ONLY for diagnostic fallback when an
// uncaught crash bubbles up to ErrorBoundary AFTER the throwing error
// reference is gone. Intentionally NOT propagated back to the server: each
// request must get its own fresh id so traces stay 1:1 with HTTP calls.
let lastRequestId: string | null = null;

/** Tetto d'attesa per i caricamenti di sezione con skeleton (vedi `ApiClient.get`). */
export const SECTION_LOAD_DEADLINE_MS = 20_000;
export function sectionLoadDeadline(): AbortSignal {
  return AbortSignal.timeout(SECTION_LOAD_DEADLINE_MS);
}
export function getLastRequestId(): string | null {
  return lastRequestId;
}

/**
 * Task #1237 — header interceptor pattern (sostituisce polling 60s).
 * Ogni response del backend espone gli header `X-Storage-Quota-*`. L'apiClient
 * li intercetta e dispatch un CustomEvent passivo: il widget header ascolta
 * (`useStorageQuota` listener-driven) senza pagare costo di polling extra.
 *
 * Header attesi (round 4 fix #3 — naming canonico contratto Done criteria):
 *   - X-Storage-R2-Used-Pct, X-Storage-R2-Status
 *   - X-Storage-DB-Used-Pct, X-Storage-DB-Status
 *   - X-Storage-Blocked-Categories  (csv categorie)
 *   - X-Storage-Emergency-Until (ISO date o vuoto)
 */
export interface StorageQuotaHeaderHint {
  r2Pct: number | null;
  r2Status: string | null;
  dbPct: number | null;
  dbStatus: string | null;
  blockedCategories: string[];
  emergencyUntil: string | null;
  ts: number;
}
export const STORAGE_QUOTA_HEADER_EVENT = 'edgvoip:storage-quota-header';
function dispatchQuotaHeaders(response: Response): void {
  try {
    const r2Pct = response.headers.get('X-Storage-R2-Used-Pct');
    const r2Status = response.headers.get('X-Storage-R2-Status');
    const dbPct = response.headers.get('X-Storage-DB-Used-Pct');
    const dbStatus = response.headers.get('X-Storage-DB-Status');
    const blocked = response.headers.get('X-Storage-Blocked-Categories');
    const emergency = response.headers.get('X-Storage-Emergency-Until');
    if (!r2Pct && !dbPct && !blocked && !emergency) return;
    const hint: StorageQuotaHeaderHint = {
      r2Pct: r2Pct ? parseFloat(r2Pct) : null,
      r2Status: r2Status,
      dbPct: dbPct ? parseFloat(dbPct) : null,
      dbStatus: dbStatus,
      blockedCategories: blocked ? blocked.split(',').filter(Boolean) : [],
      emergencyUntil: emergency || null,
      ts: Date.now(),
    };
    window.dispatchEvent(new CustomEvent<StorageQuotaHeaderHint>(STORAGE_QUOTA_HEADER_EVENT, { detail: hint }));
  } catch { /* best-effort, mai bloccante */ }
}

/**
 * Format a UI-friendly error message that appends the short request id
 * suffix when available — e.g. "Errore. (Rif: 7c2a-b1d4)".
 * Use this in any `toast(...)` callsite that surfaces an API error.
 * The full id stays available on `err.requestId` for support escalations.
 */
export function formatApiErrorMessage(err: unknown, fallback: string = 'Si è verificato un errore'): string {
  // Task #2016 — preferenza al payload strutturato `{ error: { code, message_it, hint } }`
  // ritornato dagli endpoint AI Routing (Task #2015). Se presente, vince su `err.message`
  // (che contiene la stringa generica `HTTP 4xx`) per dare al toast un messaggio operatore.
  const structured = extractStructuredApiError(err);
  let base: string;
  if (structured) {
    base = structured.hint ? `${structured.message_it} — ${structured.hint}` : structured.message_it;
  } else {
    base = isApiError(err) && err.message ? err.message : fallback;
  }
  const rid = isApiError(err) ? err.requestId : undefined;
  if (!rid) return base;
  // Short suffix: first segment of UUID (8 chars) is plenty for triage.
  const short = rid.length > 8 ? rid.slice(0, 8) : rid;
  return `${base} (Rif: ${short})`;
}

/**
 * Task #2016 — estrae l'oggetto error strutturato `{ code, message_it, hint?, suggested_model? }`
 * dai response body backend AI Routing. Ritorna `null` se il payload non è in formato
 * strutturato (es. errori legacy con `error: "stringa"`). Tipizzato strict, niente `any`.
 */
export interface StructuredApiError {
  code: string;
  message_it: string;
  hint?: string;
  suggested_model?: { provider: string; model_id: string } | null;
}
export function extractStructuredApiError(err: unknown): StructuredApiError | null {
  if (!isApiError(err)) return null;
  const data = err.data;
  if (!data || typeof data !== 'object') return null;
  const errField = (data as { error?: unknown }).error;
  if (!errField || typeof errField !== 'object') return null;
  const e = errField as Record<string, unknown>;
  if (typeof e.code !== 'string' || typeof e.message_it !== 'string') return null;
  const out: StructuredApiError = { code: e.code, message_it: e.message_it };
  if (typeof e.hint === 'string') out.hint = e.hint;
  if (e.suggested_model && typeof e.suggested_model === 'object') {
    const sm = e.suggested_model as Record<string, unknown>;
    if (typeof sm.provider === 'string' && typeof sm.model_id === 'string') {
      out.suggested_model = { provider: sm.provider, model_id: sm.model_id };
    }
  }
  return out;
}

// Note: Shared types will be imported when the shared package is built

// Task #3263 — TENANT_AGNOSTIC_PREFIXES e shouldPrefixTenant vivono ora in
// @easyflow/ui-frontend (lib/api-client-core.ts), SoT unica condivisa con i
// FE verticali (frontend-w3). Nuovi prefix tenant-agnostici vanno aggiunti la'.

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private originalToken: string | null = null;
  private impersonationActive: boolean = false;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    // Initialize impersonation from localStorage on app startup
    this.initImpersonation();
  }

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  // Token effettivo per fetch manuali (blob/PDF) fuori dai metodi apiClient:
  // stessa risoluzione usata internamente dalle request.
  getAuthToken(): string {
    return this.token || localStorage.getItem('edgvoip_tenant_token') || '';
  }

  // Extract tenant slug from JWT token
  private getTenantSlugFromToken(): string | null {
    const token = this.token || localStorage.getItem('edgvoip_tenant_token');
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]!));
      return payload.tenantSlug || payload.tenant_slug || null;
    } catch {
      return null;
    }
  }

  // Generic HTTP methods
  /**
   * `init.signal` opzionale: le sezioni che mostrano uno skeleton passano
   * `sectionLoadDeadline()` così un fetch che non si conclude mai (proxy,
   * refresh single-flight, connessione persa senza reset) diventa un errore
   * esplicito con "Riprova" invece di uno spinner perenne.
   */
  async get<T = unknown>(endpoint: string, init?: Pick<RequestInit, 'signal'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', ...(init?.signal ? { signal: init.signal } : {}) });
  }

  async post<T = unknown>(
    endpoint: string,
    data?: unknown,
    init?: { headers?: Record<string, string>; signal?: AbortSignal },
  ): Promise<ApiResponse<T>> {
    // POST non era coperto dalla guardia, ed e' il metodo delle transizioni di
    // stato (`/risorsa/:id/azione`): proprio i path dove un id assente e' piu'
    // probabile e piu' dannoso, perche' la risorsa viene comunque creata.
    this.assertHasId('POST', endpoint);
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'POST',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      ...(init?.headers ? { headers: init.headers } : {}),
      // `signal` per le richieste annullabili (suggerimenti topic con debounce).
      ...(init?.signal ? { signal: init.signal } : {}),
    });
  }

  /**
   * Task #1791 — id-guard runtime per PUT/PATCH/DELETE.
   * Intercetta URL costruiti via template literal con id mancante
   * (`/collection/${editingItem?.id}` quando `id` è `undefined`/`null`),
   * che generano `/collection/undefined` o `/collection/`. Lato BE l'effetto
   * tipico è 404 (collection root) o 500 (UUID cast). Failing fast con un
   * errore esplicito evita 18 falsi positivi nei smoke + chiarisce il bug.
   */
  private assertHasId(method: string, endpoint: string): void {
    const path = endpoint.split('?')[0]!;
    // Task #1791 controllava solo l'ULTIMO segmento. Un id mancante a meta'
    // percorso sfuggiva: `/pos/ddt-passivi/null/transition` termina con
    // "transition" e passava indenne, arrivando al backend come cast UUID
    // fallito (500). Ora si scandiscono tutti i segmenti.
    const segments = path.split('/').filter(Boolean);
    const badSeg = segments.find((seg) => seg === 'undefined' || seg === 'null');
    if (badSeg) {
      const err: ApiError = new Error(`${method} ${endpoint}: id assente (segmento "${badSeg}"). Verifica lo stato del componente prima di invocare la mutazione.`);
      err.status = 400;
      throw err;
    }
    if (path.endsWith('/')) {
      const err: ApiError = new Error(`${method} ${endpoint}: id assente (URL termina con "/"). Verifica lo stato del componente prima di invocare la mutazione.`);
      err.status = 400;
      throw err;
    }
  }

  async put<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    this.assertHasId('PUT', endpoint);
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    });
  }

  async patch<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    this.assertHasId('PATCH', endpoint);
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    });
  }

  async delete<T = unknown>(endpoint: string): Promise<ApiResponse<T>> {
    this.assertHasId('DELETE', endpoint);
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Percorso comune per le risposte NON-JSON (blob, PDF, audio, SSE): stessa
   * costruzione URL (prefisso tenant), stesso Bearer, stessa cattura di
   * `X-Request-ID`/quota header e stesso refresh single-flight su 401 del
   * percorso JSON. Unico punto del client che chiama `fetch` oltre a
   * `request`: ogni superficie "speciale" (download, stream) passa da qui,
   * mai da un wrapper parallelo.
   */
  private async rawRequest(
    endpoint: string,
    options: RequestInit,
    _retryAfterRefresh: boolean = false,
  ): Promise<{ response: Response; requestId: string | null }> {
    let url: string;
    const tenantSlug = this.getTenantSlugFromToken();
    if (shouldPrefixTenant(endpoint, tenantSlug)) {
      url = `${this.baseURL}/${tenantSlug}${endpoint}`;
    } else {
      url = `${this.baseURL}${endpoint}`;
    }
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> | undefined),
    };
    const token = this.token || localStorage.getItem('edgvoip_tenant_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    if (_retryAfterRefresh && lastRequestId && !headers['X-Request-ID']) {
      headers['X-Request-ID'] = lastRequestId;
    }
    const response = await fetch(url, { ...options, headers, credentials: 'include' });
    const requestId = response.headers.get('X-Request-ID');
    if (requestId) lastRequestId = requestId;
    dispatchQuotaHeaders(response);

    if (response.status === 401 && !_retryAfterRefresh) {
      const hasRefreshToken = !!localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      const bodyRetryable = !options.body ||
        typeof options.body === 'string' ||
        (typeof FormData !== 'undefined' && options.body instanceof FormData);
      let refreshTransientFailure = false;
      if (hasRefreshToken && bodyRetryable) {
        try {
          const mod = await import('./tokenRefresh');
          const outcome = await mod.refreshToken();
          if (outcome.kind === 'refreshed') {
            return this.rawRequest(endpoint, options, true);
          }
          refreshTransientFailure = outcome.kind === 'transient';
        } catch {
          // fall through to unauthorized
        }
      }
      if (!refreshTransientFailure) {
        this.clearToken();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }
    return { response, requestId };
  }

  /** Errore tipizzato (`ApiError`) da una risposta non-2xx del percorso raw. */
  private async throwRawError(response: Response, requestId: string | null): Promise<never> {
    let payload: { error?: string | { message?: string; code?: string }; message?: string; request_id?: string } | null = null;
    try { payload = await response.json(); } catch { /* corpo non JSON */ }
    const err: ApiError = new Error(resolveApiErrorMessage(payload, response.status));
    err.status = response.status;
    err.data = payload;
    const errReqId = requestId || payload?.request_id;
    if (errReqId) err.requestId = errReqId;
    throw err;
  }

  /** POST JSON and receive a binary response (PDF preview, ZIP, CSV). */
  async postBlob(endpoint: string, body: unknown = {}): Promise<Blob> {
    return (await this.download(endpoint, { method: 'POST', body })).blob;
  }

  /** Download a binary response (PDF/XML/P7M). Throws { status, data } on non-2xx. */
  async getBlob(endpoint: string): Promise<Blob> {
    return (await this.download(endpoint)).blob;
  }

  /**
   * Download autenticato con metadati: blob + nome file suggerito dal server
   * (`Content-Disposition`) + content-type. Per export/PDF/audio dove il
   * chiamante salva o apre il file. `body` JSON solo con `method: 'POST'`.
   */
  async download(
    endpoint: string,
    init: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal } = {},
  ): Promise<{ blob: Blob; filename: string | null; contentType: string | null; response: Response }> {
    const method = init.method ?? 'GET';
    const options: RequestInit = { method };
    if (method === 'POST') {
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify(init.body ?? {});
    }
    if (init.signal) options.signal = init.signal;
    const { response, requestId } = await this.rawRequest(endpoint, options);
    if (!response.ok) await this.throwRawError(response, requestId);
    const cd = response.headers.get('content-disposition') || '';
    const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    let filename: string | null = null;
    if (m && m[1]) {
      try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
    }
    return {
      blob: await response.blob(),
      filename,
      contentType: response.headers.get('content-type'),
      response,
    };
  }

  /**
   * Richiesta autenticata che restituisce la `Response` grezza per i corpi
   * letti in streaming (SSE via `fetch`, `response.body.getReader()`). Il
   * chiamante gestisce il reader e l'`AbortSignal`; errori HTTP tipizzati
   * come gli altri metodi.
   */
  async stream(
    endpoint: string,
    init: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal; headers?: Record<string, string> } = {},
  ): Promise<Response> {
    const method = init.method ?? 'GET';
    const headers: Record<string, string> = { Accept: 'text/event-stream', ...(init.headers ?? {}) };
    const options: RequestInit = { method, headers };
    if (init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(init.body);
    }
    if (init.signal) options.signal = init.signal;
    const { response, requestId } = await this.rawRequest(endpoint, options);
    if (!response.ok) await this.throwRawError(response, requestId);
    return response;
  }

  /**
   * Endpoint che rispondono col payload TOP-LEVEL (`{ success, files }`,
   * `{ success, quota }`) invece dell'envelope `{ success, data }`: il DTO `T`
   * dichiara la shape reale. Versione pubblica di `requestTopLevel` per i
   * call-site migrati da `fetch` diretto (lettura single-level).
   */
  async getTopLevel<T>(endpoint: string, init?: Pick<RequestInit, 'signal'>): Promise<T> {
    return this.requestTopLevel<T>(endpoint, { method: 'GET', ...(init?.signal ? { signal: init.signal } : {}) });
  }

  async postTopLevel<T>(endpoint: string, data?: unknown): Promise<T> {
    this.assertHasId('POST', endpoint);
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.requestTopLevel<T>(endpoint, {
      method: 'POST',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    });
  }

  async putTopLevel<T>(endpoint: string, data?: unknown): Promise<T> {
    this.assertHasId('PUT', endpoint);
    return this.requestTopLevel<T>(endpoint, { method: 'PUT', body: data ? JSON.stringify(data) : undefined });
  }

  async patchTopLevel<T>(endpoint: string, data?: unknown): Promise<T> {
    this.assertHasId('PATCH', endpoint);
    return this.requestTopLevel<T>(endpoint, { method: 'PATCH', body: data ? JSON.stringify(data) : undefined });
  }

  async deleteTopLevel<T>(endpoint: string): Promise<T> {
    this.assertHasId('DELETE', endpoint);
    return this.requestTopLevel<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Task #3713 — endpoint legacy che rispondono col payload TOP-LEVEL
   * (es. `{ success, links }`, `{ workflows, total }`) invece dell'envelope
   * `{ success, data }` modellato da `ApiResponse<T>`. Unico boundary-cast
   * del client: il DTO `T` dichiara la shape reale osservata dal backend.
   */
  private async requestTopLevel<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return await this.request(endpoint, options) as unknown as T;
  }

  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
    _retryAfterRefresh: boolean = false
  ): Promise<ApiResponse<T>> {
    // Build URL with tenant slug if token is available and endpoint doesn't start with /auth or /health
    let url: string;
    const tenantSlug = this.getTenantSlugFromToken();

    if (shouldPrefixTenant(endpoint, tenantSlug)) {
      // Insert tenant slug between baseURL and endpoint
      // /api + /demo + /cdr = /api/demo/cdr
      url = `${this.baseURL}/${tenantSlug}${endpoint}`;
    } else {
      url = `${this.baseURL}${endpoint}`;
    }

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers as Record<string, string> | undefined),
    };

    // Get token from instance or localStorage as fallback
    const token = this.token || localStorage.getItem('edgvoip_tenant_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Task #1245 — propagate `X-Request-ID` ONLY on a retry chain. We must
    // NOT send a sticky `lastRequestId` on every fresh request (that would
    // collapse unrelated requests into one trace). On a token-refresh
    // retry (`_retryAfterRefresh=true`), echoing the id of the failed
    // attempt lets the backend correlate "user retried action X" across
    // log lines and DB queries. Honored by the backend `requestId`
    // middleware via its inbound-preserve branch.
    if (_retryAfterRefresh && lastRequestId && !headers['X-Request-ID']) {
      headers['X-Request-ID'] = lastRequestId;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      // Task #1245 — capture response request id (set by every backend
      // response, success or error). Stored module-level for diagnostic
      // display and attached to thrown errors below.
      const responseRequestId = response.headers.get('X-Request-ID');
      if (responseRequestId) lastRequestId = responseRequestId;
      // Task #1237 — header interceptor quota (passive, listener-driven)
      dispatchQuotaHeaders(response);

      // Handle 204 No Content (no JSON body)
      let data;
      if (response.status === 204) {
        data = null;
      } else {
        const text = await response.text();

        if (!response.ok) {
          if (response.status === 401) {
            // Some upstream/business-level 401s (e.g. A-Cube unauthorized) are
            // NOT session-auth failures. They share the HTTP status with the
            // upstream API for semantic correctness, but we must not log the
            // user out of our app. We peek at the typed error envelope
            // (`{error:{code:'acube_unauthorized'}}`) before deciding.
            let isBusinessLevel401 = false;
            try {
              const peekText = text || '';
              const peekCt = response.headers.get('content-type') || '';
              if (peekText && peekCt.includes('application/json')) {
                const peekJson = JSON.parse(peekText);
                const code = peekJson?.error?.code;
                if (typeof code === 'string' && code.startsWith('acube_')) {
                  isBusinessLevel401 = true;
                }
              }
            } catch {
              // non-JSON body → treat as session-auth failure (default behaviour)
            }

            if (!isBusinessLevel401) {
              const isAuthEndpoint = endpoint.startsWith('/auth') ||
                endpoint.includes('/login') ||
                endpoint.includes('/refresh-token') ||
                endpoint.includes('/logout');
              const hasRefreshToken = !!localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
              const bodyRetryable = !options.body ||
                typeof options.body === 'string' ||
                (typeof FormData !== 'undefined' && options.body instanceof FormData) ||
                (typeof URLSearchParams !== 'undefined' && options.body instanceof URLSearchParams);

              let refreshTransientFailure = false;
              if (!_retryAfterRefresh && !isAuthEndpoint && hasRefreshToken && bodyRetryable) {
                try {
                  const mod = await import('./tokenRefresh');
                  const outcome = await mod.refreshToken();
                  if (outcome.kind === 'refreshed') {
                    return this.request<T>(endpoint, options, true);
                  }
                  // Errore temporaneo (rete/5xx/timeout): la sessione non è
                  // terminata, la richiesta fallisce senza sloggare l'utente.
                  refreshTransientFailure = outcome.kind === 'transient';
                } catch {
                  // fall through to unauthorized
                }
              }

              if (!refreshTransientFailure) {
                this.clearToken();
                window.dispatchEvent(new CustomEvent('auth:unauthorized'));
              }
            }
          }

          if (response.status >= 502 && response.status <= 504) {
            const contentType = response.headers.get('content-type') || '';
            const trustedError = trustedGatewayApplicationError(text, contentType);
            if (trustedError) {
              const err: ApiError = new Error(trustedError.message);
              err.status = response.status;
              err.data = trustedError.payload;
              const errReqId = responseRequestId || (
                typeof trustedError.payload.request_id === 'string'
                  ? trustedError.payload.request_id
                  : undefined
              );
              if (errReqId) err.requestId = errReqId;
              throw err;
            }
            const err: ApiError = new Error('Servizio temporaneamente non disponibile. Riprova tra qualche secondo.');
            err.status = response.status;
            if (responseRequestId) err.requestId = responseRequestId;
            throw err;
          }

          let errorData: {
            error?: string | { message?: string; code?: string };
            module?: string;
            request_id?: string;
            [key: string]: unknown;
          } | null = null;
          const contentType = response.headers.get('content-type') || '';
          if (text && contentType.includes('application/json')) {
            try {
              errorData = JSON.parse(text);
            } catch {
              // non-JSON error body, ignore parse failure
            }
          }
          // Task #1527 — toast bloccante per quote sature: dispatch evento
          // ascoltato dal provider in App.tsx. Il backend marca le 422
          // bloccanti con error_code STORAGE_QUOTA_EXCEEDED |
          // DB_QUOTA_EXCEEDED_EMBEDDINGS.
          if (response.status === 422 && errorData && typeof errorData === 'object') {
            const code = (errorData as { error_code?: unknown; code?: unknown }).error_code
              ?? (errorData as { code?: unknown }).code;
            if (code === 'STORAGE_QUOTA_EXCEEDED' || code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS') {
              try {
                window.dispatchEvent(new CustomEvent('edgvoip:storage-quota-error', {
                  detail: {
                    errorCode: code,
                    message: typeof errorData?.error === 'string' ? errorData.error : `Quota raggiunta (${code})`,
                    scope: code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS' ? 'db' : 'r2',
                    blockedCategory: code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS' ? 'embedding' : 'upload',
                    pct: typeof (errorData as { pct?: unknown }).pct === 'number' ? (errorData as { pct: number }).pct : null,
                    requestId: responseRequestId ?? null,
                  },
                }));
              } catch { /* SSR-safe */ }
            }
          }
          if (!contentType.includes('application/json') && text) {
            const trimmed = text.trimStart();
            if (trimmed.startsWith('<') || trimmed.startsWith('<!DOCTYPE')) {
              const err: ApiError = new Error(`Errore del server (HTTP ${response.status}). Riprova più tardi.`);
              err.status = response.status;
              if (responseRequestId) err.requestId = responseRequestId;
              throw err;
            }
          }

          if (response.status === 403) {
            const moduleInfo = errorData?.module;
            // Testo del server da entrambe le buste (`error` stringa o
            // envelope canonico `error.message`); «Accesso negato» solo
            // quando il corpo non porta alcun messaggio.
            const resolved403 = resolveApiErrorMessage(errorData, 403);
            const errorMsg = moduleInfo
              ? `Modulo "${moduleInfo}" non disponibile per questo tenant.`
              : (resolved403 === 'HTTP 403' ? 'Accesso negato' : resolved403);
            if (moduleInfo) {
              window.dispatchEvent(new CustomEvent('api:module-disabled', { detail: { message: errorMsg, module: moduleInfo } }));
            }
            const err: ApiError = new Error(errorMsg);
            (err as ModuleDisabledError).status = 403;
            // Task #1435 — preserva il payload (code/current/limit) così i call
            // site di creazione utenti/agenti AI possono distinguere il 403
            // "limite tenant raggiunto" e mostrare il modal dedicato.
            if (errorData && typeof errorData === 'object') {
              err.data = errorData;
            }
            if (responseRequestId) err.requestId = responseRequestId;
            // Task #1435 — segnala globalmente i 403 di limite tenant: un
            // listener montato in App apre il modal contestuale anche quando
            // il call site non lo gestisce esplicitamente.
            const code = (errorData as { code?: unknown } | null | undefined)?.code;
            if (code === 'tenant_user_limit_reached' || code === 'tenant_ai_agent_limit_reached') {
              window.dispatchEvent(new CustomEvent('api:tenant-limit-reached', {
                detail: {
                  code,
                  current: Number((errorData as { current?: unknown }).current ?? 0),
                  limit: Number((errorData as { limit?: unknown }).limit ?? 0),
                  message: typeof errorData?.error === 'string' ? errorData.error : errorMsg,
                },
              }));
            }
            throw err;
          }

          // Messaggio del server (busta canonica `error` o `message` top-level,
          // es. 423 `inventory_locked`); «HTTP <status>» solo senza testo.
          const err: ApiError = new Error(resolveApiErrorMessage(errorData, response.status));
          err.status = response.status;
          if (errorData && typeof errorData === 'object') {
            err.data = errorData;
          }
          // Task #1245 — surface the request id on the thrown Error so any
          // toast/handler can show it. Prefer the response header (always
          // present), fall back to the `request_id` field in the JSON body.
          const errReqId = responseRequestId || errorData?.request_id;
          if (errReqId) err.requestId = errReqId;
          throw err;
        }

        if (text) {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            data = JSON.parse(text);
          } else {
            try {
              data = JSON.parse(text);
            } catch {
              data = null;
            }
          }
        } else {
          data = null;
        }
      }

      if (data && typeof data === 'object' && data.error && typeof data.error !== 'string') {
        const structuredError: unknown = data.error;
        if (typeof structuredError === 'object' && structuredError !== null) {
          const errorCode = 'code' in structuredError ? structuredError.code : undefined;
          if (typeof errorCode === 'string') data.error_code = errorCode;
          const errorMessage = 'message' in structuredError ? structuredError.message : undefined;
          data.error = typeof errorMessage === 'string'
            ? errorMessage
            : typeof errorCode === 'string' ? errorCode : JSON.stringify(structuredError);
        } else {
          data.error = JSON.stringify(structuredError);
        }
      }

      // Notification preferences side-effect: any successful write to the
      // notification preferences endpoints (unified or legacy) must refresh
      // the IndexedDB snapshot read by the Service Worker. Otherwise the SW
      // gates pushes against stale prefs until the next page reload.
      // See packages/frontend/src/lib/notificationPrefsSync.ts.
      try {
        const method = (options.method || 'GET').toUpperCase();
        const isWrite = method === 'PUT' || method === 'POST' || method === 'PATCH';
        const touchesNotifPrefs = endpoint.startsWith('/notifications/preferences')
          || endpoint.startsWith('/notifications/calendar-preferences');
        if (isWrite && touchesNotifPrefs && response.ok) {
          import('./notificationPrefsSync')
            .then((m) => m.syncNotificationPrefsToSW().catch(() => { /* noop */ }))
            .catch(() => { /* noop */ });
        }
      } catch { /* never let the side-effect break the request */ }

      return data;
    } catch (error: unknown) {
      if (error instanceof Error && (error as ApiError).status) {
        throw error;
      }
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new Error(`Nessuna risposta dal server entro ${Math.round(SECTION_LOAD_DEADLINE_MS / 1000)} secondi. Riprova.`);
      }
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Health check
  async health(): Promise<ApiResponse> {
    return this.request('/health');
  }

  // Tenants
  async getTenants(params?: { page?: number; limit?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.search) query.append('search', params.search);

    return this.request<TenantsListData>(`/tenants?${query.toString()}`);
  }

  async getTenant(id: string) {
    return this.request(`/tenants/${id}`);
  }

  async createTenant(data: { name: string; domain: string; edg_suite_id: string }) {
    return this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTenant(id: string, data: Partial<{ name: string; domain: string; edg_suite_id: string }>) {
    return this.request(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTenant(id: string) {
    return this.request(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  // Stores
  async getStores(params?: { page?: number; limit?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.search) query.append('search', params.search);

    return this.request(`/stores?${query.toString()}`);
  }

  async getStore(id: string) {
    return this.request(`/stores/${id}`);
  }

  async createStore(data: { name: string; store_id: string; sip_trunk_settings?: StoreSipTrunkSettings }) {
    return this.request('/stores', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStore(id: string, data: Partial<{ name: string; store_id: string; sip_trunk_settings?: StoreSipTrunkSettings }>) {
    return this.request(`/stores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteStore(id: string) {
    return this.request(`/stores/${id}`, {
      method: 'DELETE',
    });
  }

  // VoIP Entities - Extensions
  // Parametri tipizzati dal contratto condiviso: il filtro testuale è `q`
  // (il BE accetta `search` solo come alias deprecato).
  async getExtensions(params?: SipExtensionListQueryInput) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.q) query.append('q', params.q);

    return this.request<SipExtensionsListData>(`/voip/sip-extensions?${query.toString()}`);
  }

  /** Lookup esatto per numero interno (404 se assente nel tenant): niente filtro parziale né limite di pagina. */
  async getExtensionByNumber(extension: string) {
    return this.request<SipExtensionReadDto>(`/voip/sip-extensions/number/${encodeURIComponent(extension)}`);
  }

  // Liste VoIP senza filtro lato server (contratto condiviso `voipEntityListQuerySchema`,
  // strict): il BE restituisce l'elenco completo del tenant e rifiuta con 400 ogni
  // parametro di query. Niente `page/limit/store_id/search` finti qui: quando una
  // lista acquisirà un filtro reale, estendere lo schema in @easyflow/shared.
  // VoIP Entities - SIP Trunks
  async getSipTrunks() {
    return this.request<SipTrunkWire[]>('/sip-trunks');
  }

  // VoIP Entities - IVR Menus
  async getIvrMenus() {
    return this.request<IvrMenu[]>('/voip/ivr-menus');
  }

  async createIvrMenu(data: { name: string; description?: string; greeting_message?: string; store_id?: string }) {
    return this.request('/voip/ivr-menus', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Ring Groups
  async getRingGroups() {
    return this.request<RingGroup[]>('/voip/ring-groups');
  }

  async createRingGroup(data: { name: string; extension_number: string; description?: string; store_id?: string }) {
    return this.request('/voip/ring-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Queues
  async getQueues() {
    return this.request<Queue[]>('/voip/queues');
  }

  async createQueue(data: { name: string; extension_number: string; description?: string; store_id?: string }) {
    return this.request('/voip/queues', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Conference Rooms
  async getConferenceRooms() {
    return this.request<ConferenceRoom[]>('/voip/conference-rooms');
  }

  async createConferenceRoom(data: { name: string; extension_number: string; description?: string; store_id?: string }) {
    return this.request('/voip/conference-rooms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Voicemail Boxes
  async getVoicemailBoxes() {
    return this.request<VoicemailBox[]>('/voip/voicemail-boxes');
  }

  async createVoicemailBox(data: { extension_number: string; password: string; display_name: string; email_address?: string; store_id?: string }) {
    return this.request('/voip/voicemail-boxes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // CDR and Activity Logs
  async getCdrs(params?: { store_id?: string; start_date?: string; end_date?: string; direction?: string; disposition?: string; ext_number?: string; did_e164?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });

    return this.request<CdrRecord[]>(`/cdr?${query.toString()}`);
  }

  async getCdrStats(params?: { store_id?: string; start_date?: string; end_date?: string }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });

    return this.request(`/cdr/stats?${query.toString()}`);
  }

  async getActivityLogs(params?: { actor?: string; action?: string; target_type?: string; target_id?: string; status?: string; start_date?: string; end_date?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });

    return this.request(`/cdr-activity/activity-log?${query.toString()}`);
  }

  async getActivityStats(params?: { start_date?: string; end_date?: string }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });

    return this.request(`/cdr-activity/activity-log/stats?${query.toString()}`);
  }

  async getExtension(id: string) {
    return this.request(`/extensions/${id}`);
  }

  async createExtension(data: {
    store_id?: string;
    extension_number: string;
    password: string;
    caller_id_name?: string;
    caller_id_number?: string;
    enabled?: boolean;
  }) {
    return this.request('/extensions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExtension(id: string, data: Partial<{
    store_id?: string;
    extension_number: string;
    password: string;
    caller_id_name?: string;
    caller_id_number?: string;
    enabled?: boolean;
  }>) {
    return this.request(`/extensions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExtension(id: string) {
    return this.request(`/extensions/${id}`, {
      method: 'DELETE',
    });
  }

  // Users
  async getUsers() {
    return this.request('/users');
  }

  async createUser(data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'admin' | 'agent' | 'user';
  }) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: Partial<{
    first_name: string;
    last_name: string;
    role: 'admin' | 'agent' | 'user';
    status: 'active' | 'inactive' | 'suspended';
  }>) {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // CDR
  async getCDR(params?: {
    page?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    call_direction?: string;
    caller_number?: string;
    callee_number?: string;
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value.toString());
    });

    return this.request<CdrRecord[]>(`/cdr?${query.toString()}`);
  }

  async getCDRStats(params?: {
    start_date?: string;
    end_date?: string;
    call_direction?: string;
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value.toString());
    });

    return this.request(`/cdr/stats?${query.toString()}`);
  }

  // GET /cdr/export — format=json → array nudo di CdrRecord (nessun envelope),
  // format=csv → testo CSV. Overload per dare al chiamante il tipo giusto.
  async exportCDR(format: 'json', params?: Record<string, string | number | boolean | null | undefined>): Promise<CdrRecord[]>;
  async exportCDR(format: 'csv', params?: Record<string, string | number | boolean | null | undefined>): Promise<string>;
  async exportCDR(format: 'csv' | 'json', params?: Record<string, string | number | boolean | null | undefined>): Promise<CdrRecord[] | string>;
  async exportCDR(format: 'csv' | 'json', params?: Record<string, string | number | boolean | null | undefined>): Promise<CdrRecord[] | string> {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value.toString());
    });

    query.append('format', format);

    const tenantSlug = this.getTenantSlugFromToken();
    const endpoint = '/cdr/export';
    let url: string;

    // Construct URL with tenant context
    if (tenantSlug) {
      url = `${this.baseURL}/${tenantSlug}${endpoint}?${query.toString()}`;
    } else {
      url = `${this.baseURL}${endpoint}?${query.toString()}`;
    }

    const headers: Record<string, string> = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }

      // Handle based on format
      if (format === 'json') {
        return await response.json() as CdrRecord[];
      } else {
        // Return text for CSV (CSV is just a string)
        return await response.text();
      }
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  }

  async originateCall(data: {
    caller_extension: string;
    callee_number: string;
    campaign_id?: string | null;
    options?: {
      timeout?: number;
      caller_id?: string;
      context?: string;
      recording?: boolean;
    };
  }) {
    // Task #4700 — contratto condiviso `OriginateCallRequestSchema`: il dominio
    // SIP è derivato dal tenant autenticato, non viene più inviato dal client.
    return this.request('/calls/originate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async transferCall(data: {
    call_uuid: string;
    destination: string;
    type?: 'attended' | 'blind';
  }) {
    return this.request('/calls/transfer', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async hangupCall(data: {
    call_uuid: string;
    cause?: string;
  }) {
    return this.request('/calls/hangup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async holdCall(data: {
    call_uuid: string;
    hold?: boolean;
  }) {
    return this.request('/calls/hold', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Task #4702 — mute/record/info/status rimossi: le rotte backend erano
  // morte (metodi assenti su ESLService → 500) e senza chiamanti. Per la
  // registrazione usare `/voip/calls/recording`.

  // ===== SUPER ADMIN & TENANT MANAGEMENT =====

  // Cross-tenant statistics
  // Task #3266 — filtro opzionale per prodotto/edition (`tenants.product`).
  async getCrossTenantStats(product?: string) {
    return this.request(`/tenants/stats${product ? `?product=${encodeURIComponent(product)}` : ''}`);
  }

  async getTenantStatsList(product?: string) {
    return this.request(`/tenants/stats-list${product ? `?product=${encodeURIComponent(product)}` : ''}`);
  }

  // Tenant management with companies, contacts and admin user
  async createTenantWithCompanies(data: {
    name: string;
    slug: string;
    domain: string;
    sip_domain: string;
    admin_user: {
      first_name: string;
      last_name: string;
      email: string;
      password: string;
      role: 'admin' | 'super_admin';
    };
    companies: Array<{
      legal_name: string;
      vat_number?: string;
      tax_code?: string;
      address?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
      is_primary: boolean;
    }>;
    contacts: Array<{
      first_name: string;
      last_name: string;
      role?: string;
      email?: string;
      phone?: string;
      mobile?: string;
      is_primary: boolean;
    }>;
  }) {
    return this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTenantWithDetails(id: string) {
    return this.request(`/tenants/${id}`);
  }

  async updateTenantWithDetails(id: string, data: Record<string, unknown>) {
    return this.request(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async activateTenant(id: string) {
    return this.request(`/tenants/${id}/activate`, {
      method: 'POST',
    });
  }

  async suspendTenant(id: string) {
    return this.request(`/tenants/${id}/suspend`, {
      method: 'POST',
    });
  }

  // User impersonation
  async impersonateUser(tenantId: string, userId: string) {
    return this.request(`/tenants/${tenantId}/impersonate`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  // Cross-tenant analytics
  async getCrossTenantCalls(params?: {
    start_date?: string;
    end_date?: string;
    tenant_id?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });

    return this.request(`/analytics/cross-tenant/calls?${query.toString()}`);
  }

  async getCrossTenantExtensions() {
    return this.request('/analytics/cross-tenant/extensions');
  }

  async getCrossTenantLiveCalls() {
    return this.request('/analytics/cross-tenant/live-calls');
  }

  async getCrossTenantUsers() {
    return this.request('/analytics/cross-tenant/users');
  }

  async getCrossTenantCompanies() {
    return this.request('/analytics/cross-tenant/companies');
  }

  async getCrossTenantSummary(params?: {
    period?: '1h' | '24h' | '7d' | '30d';
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });

    return this.request(`/analytics/cross-tenant/summary?${query.toString()}`);
  }

  // Impersonation management methods
  initImpersonation() {
    // Check if there's an impersonation token from the master frontend
    const impersonationToken = localStorage.getItem('edgvoip_impersonation_token');
    if (impersonationToken) {
      // Save original token (if any) and use impersonation token
      this.originalToken = this.token;
      this.token = impersonationToken;
      this.impersonationActive = true;
    }
  }

  startImpersonation(impersonationToken: string) {
    // Save original token
    this.originalToken = this.token;
    // Set impersonation token
    this.token = impersonationToken;
    this.impersonationActive = true;
    // Save to localStorage
    localStorage.setItem('edgvoip_impersonation_token', impersonationToken);
  }

  exitImpersonation() {
    // Get master's backup token if available
    const masterBackupToken = localStorage.getItem('edgvoip_master_backup_token');
    if (masterBackupToken) {
      this.token = masterBackupToken;
    } else if (this.originalToken) {
      this.token = this.originalToken;
    }
    this.originalToken = null;
    this.impersonationActive = false;
    // Clear localStorage
    localStorage.removeItem('edgvoip_impersonation_token');
    localStorage.removeItem('edgvoip_impersonation_info');
  }

  isImpersonating(): boolean {
    return this.impersonationActive || localStorage.getItem('edgvoip_impersonation_token') !== null;
  }

  // ── Campaign ↔ Workflow Builder ──────────────────────────────────────────

  async getCampaignWorkflows(campaignId: string) {
    return workflowCampaignStateSchema.parse(await this.requestTopLevel<unknown>(`/crm/campaigns/${campaignId}/workflows`));
  }

  async stopCampaignWorkflow(campaignId: string, linkId: string, body: { generation: number; mode: 'new_entries' | 'all_pending' }) {
    return workflowStopResultSchema.parse(await this.requestTopLevel<unknown>(`/crm/campaigns/${campaignId}/workflows/${linkId}/stop`, {
      method: 'POST', body: JSON.stringify(body),
    }));
  }

  async restoreCampaignWorkflowRule(campaignId: string, ruleId: string) {
    return workflowRuleRestoreResultSchema.parse(await this.requestTopLevel<unknown>(`/crm/campaigns/${campaignId}/workflow-rule-restorations`, {
      method: 'POST', body: JSON.stringify({ rule_id: ruleId }),
    }));
  }

  async getAllWorkflowLinks() {
    return this.requestTopLevel<CrmCampaignWorkflowLinksResponse>('/crm/campaigns/all/workflow-links');
  }

  async getAllWorkflowRuns(params?: { limit?: number; offset?: number; status?: string; workflow_id?: string; search?: string }) {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.status) q.set('status', params.status);
    if (params?.workflow_id) q.set('workflow_id', params.workflow_id);
    if (params?.search) q.set('search', params.search);
    return crmAllWorkflowRunsResponseSchema.parse(await this.requestTopLevel<unknown>(`/crm/campaigns/all/workflow-runs?${q.toString()}`));
  }

  async updateCampaignWorkflow(campaignId: string, triggerId: string, body: {
    is_active?: boolean;
  }) {
    return this.request(`/crm/campaigns/${campaignId}/workflows/${triggerId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async removeCampaignWorkflow(campaignId: string, triggerId: string) {
    return this.request(`/crm/campaigns/${campaignId}/workflows/${triggerId}`, {
      method: 'DELETE',
    });
  }

  async getCampaignWorkflowStats(campaignId: string) {
    return this.requestTopLevel<CrmCampaignWorkflowStatsResponse>(`/crm/campaigns/${campaignId}/workflow-runs/stats`);
  }

  async getCampaignWorkflowRuns(campaignId: string, params?: {
    limit?: number;
    offset?: number;
    workflow_id?: string;
  }) {
    const q = new URLSearchParams();
    if (params?.limit)       q.set('limit',       String(params.limit));
    if (params?.offset)      q.set('offset',      String(params.offset));
    if (params?.workflow_id) q.set('workflow_id', params.workflow_id);
    const qs = q.toString();
    return this.requestTopLevel<CrmCampaignWorkflowRunsResponse>(`/crm/campaigns/${campaignId}/workflow-runs${qs ? `?${qs}` : ''}`);
  }

  // GET /workflows — risposta BARE { workflows, total, limit, offset } (nessun envelope).
  async getWorkflowsList(params: { search?: string; limit?: number; offset?: number; campaign_id?: string } = {}) {
    const q = new URLSearchParams({ limit: String(params.limit ?? 200), offset: String(params.offset ?? 0) });
    if (params.search) q.set('search', params.search);
    if (params.campaign_id) q.set('campaign_id', params.campaign_id);
    return workflowsListResponseSchema.parse(await this.requestTopLevel<unknown>(`/workflows?${q}`));
  }

  async getWorkflowDefinition(id: string) {
    return workflowEditorResponseSchema.parse(await this.requestTopLevel<unknown>(`/workflows/${id}`)).workflow;
  }

  async saveWorkflowDefinition(id: string | null, payload: Record<string, unknown>) {
    return workflowSavedResponseSchema.parse(await this.requestTopLevel<unknown>(id ? `/workflows/${id}` : '/workflows', {
      method: id ? 'PUT' : 'POST', body: JSON.stringify(payload),
    })).workflow;
  }

  async runSavedWorkflow(id: string) {
    return workflowRunStartSchema.parse(await this.requestTopLevel<unknown>(`/workflows/${id}/run`, {
      method: 'POST', body: JSON.stringify({ trigger_data: {} }),
    }));
  }

  async getWorkflowRun(id: string) {
    return workflowRunDetailSchema.parse(await this.requestTopLevel<unknown>(`/workflows/runs/${id}`));
  }
  async getWorkflowRunsPage(workflowId: string, limit: number, offset: number) {
    return workflowSavedRunsResponseSchema.parse(await this.requestTopLevel<unknown>(
      `/workflows/runs?${new URLSearchParams({ workflow_id: workflowId, limit: String(limit), offset: String(offset) })}`));
  }

  async cloneWorkflowDefinition(id: string, name: string) {
    return workflowEditorDefinitionSchema.parse(await this.requestTopLevel<unknown>(`/workflows/${id}/clone`, {
      method: 'POST', body: JSON.stringify({ name }),
    }));
  }

  // ── Board ↔ Workflow Builder ──────────────────────────────────────────

  async getBoardWorkflows(boardId: string) {
    return this.requestTopLevel<BoardWorkflowTriggersResponse>(`/tasks/boards/${boardId}/workflows`);
  }

  async addBoardWorkflow(boardId: string, body: { workflow_id: string }) {
    return this.request(`/tasks/boards/${boardId}/workflows`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateBoardWorkflow(boardId: string, triggerId: string, body: { is_active?: boolean }) {
    return this.request(`/tasks/boards/${boardId}/workflows/${triggerId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async removeBoardWorkflow(boardId: string, triggerId: string) {
    return this.request(`/tasks/boards/${boardId}/workflows/${triggerId}`, {
      method: 'DELETE',
    });
  }

  async getBoardWorkflowStats(boardId: string) {
    return this.requestTopLevel<BoardWorkflowStatsResponse>(`/tasks/boards/${boardId}/workflow-runs/stats`);
  }

  async getBoardWorkflowRuns(boardId: string, params?: {
    limit?: number;
    offset?: number;
    workflow_id?: string;
  }) {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.workflow_id) q.set('workflow_id', params.workflow_id);
    const qs = q.toString();
    return this.requestTopLevel<BoardWorkflowRunsResponse>(`/tasks/boards/${boardId}/workflow-runs${qs ? `?${qs}` : ''}`);
  }

  // ── Task Settings Dashboard (global links) ──────────────────────────────
  
  // Risposte TOP-LEVEL { success, links } / { success, runs } (nessun envelope data).
  async getTaskWorkflowLinks() {
    return this.requestTopLevel<TaskSettingsWorkflowLinksResponse>('/tasks/settings/workflow-links');
  }

  async getTaskFormLinks() {
    return this.requestTopLevel<TaskSettingsFormLinksResponse>('/tasks/settings/form-links');
  }

  async getTaskAutomationLinks() {
    return this.requestTopLevel<TaskSettingsAutomationLinksResponse>('/tasks/settings/automation-links');
  }

  async getTaskWorkflowRuns(params?: { limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.requestTopLevel<TaskSettingsWorkflowRunsResponse>(`/tasks/settings/workflow-runs${qs ? `?${qs}` : ''}`);
  }

  async removeTaskWorkflowLink(triggerId: string) {
    return this.request(`/tasks/settings/workflow-links/${triggerId}`, { method: 'DELETE' });
  }

  async removeTaskFormLink(linkId: string) {
    return this.request(`/tasks/settings/form-links/${linkId}`, { method: 'DELETE' });
  }

  async removeTaskAutomationLink(linkId: string) {
    return this.request(`/tasks/settings/automation-links/${linkId}`, { method: 'DELETE' });
  }

  // Register a pending campaign call - used by click-to-call in campaign context
  async registerCampaignCall(campaignId: string, phoneNumber: string, callerExtension?: string, leadId?: string) {
    return this.request(`/crm/campaigns/${campaignId}/register-call`, {
      method: 'POST',
      body: JSON.stringify({ 
        phone_number: phoneNumber,
        caller_extension: callerExtension,
        lead_id: leadId || undefined
      }),
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;

// ============================================================================
// Task #1351 v2 — Wrapper tipizzati Lifecycle + Snapshot prodotto magazzino.
// I DTO vivono in `@easyflow/shared/types/api`. NON usare `any`. Le rotte sono
// montate sotto `tenantScopedRouter` legacy (`/pos/...`), quindi `apiClient`
// applica automaticamente il prefisso slug.
// ============================================================================

export type ProductLifecycleEvent = LifecycleEventDto;
export type ProductLifecyclePage = LifecyclePageDto;
export type ProductLifecycleQuery = LifecycleQueryParamsDto;
export type ProductSnapshotResponse = ProductSnapshotDto;

function buildLifecycleQs(q: LifecycleQueryParamsDto = {}): string {
  const p = new URLSearchParams();
  if (q.limit != null) p.set('limit', String(q.limit));
  if (q.cursor) p.set('cursor', q.cursor);
  if (q.warehouse_id) p.set('warehouse_id', q.warehouse_id);
  if (q.lot_id) p.set('lot_id', q.lot_id);
  if (q.serial_id) p.set('serial_id', q.serial_id);
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.event_types && q.event_types.length > 0) p.set('event_types', q.event_types.join(','));
  if (q.movement_types && q.movement_types.length > 0) p.set('movement_types', q.movement_types.join(','));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function getProductLifecycle(
  productId: string,
  query: LifecycleQueryParamsDto = {},
): Promise<ApiResponse<LifecyclePageDto>> {
  return apiClient.get<LifecyclePageDto>(
    `/pos/warehouse/products/${encodeURIComponent(productId)}/lifecycle${buildLifecycleQs(query)}`,
  );
}

export async function getProductSnapshot(
  productId: string,
  at?: string | null,
  warehouseId?: string | null,
): Promise<ApiResponse<ProductSnapshotDto>> {
  const params: string[] = [];
  if (at) params.push(`at=${encodeURIComponent(at)}`);
  if (warehouseId) params.push(`warehouse_id=${encodeURIComponent(warehouseId)}`);
  const qs = params.length > 0 ? `?${params.join('&')}` : '';
  return apiClient.get<ProductSnapshotDto>(
    `/pos/warehouse/products/${encodeURIComponent(productId)}/snapshot${qs}`,
  );
}

/**
 * Task #3118 — report riconciliazione giacenza ↔ ledger (dry-run, sola lettura).
 * `productId` opzionale limita al singolo prodotto, altrimenti intero tenant.
 */
export async function getStockReconciliationReport(
  productId?: string | null,
): Promise<ApiResponse<StockReconciliationReportDto>> {
  const qs = productId ? `?product_id=${encodeURIComponent(productId)}` : '';
  return apiClient.get<StockReconciliationReportDto>(
    `/pos/warehouse/stock-reconciliation${qs}`,
  );
}

/**
 * Task #3118 — correzione riconciliazione. `apply=false` (default) è dry-run;
 * `apply=true` riallinea `pos_stock` al ledger (bucket `in_stock`, solo dove sicuro).
 */
export async function applyStockReconciliation(
  opts: { productId?: string | null; apply?: boolean } = {},
): Promise<ApiResponse<StockReconciliationReportDto>> {
  return apiClient.post<StockReconciliationReportDto>(
    `/pos/warehouse/stock-reconciliation`,
    { product_id: opts.productId ?? undefined, apply: opts.apply === true },
  );
}
